import * as React from 'react'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/utils/cn'

export interface StepperStep {
  id: string
  label: string
}

export interface StepperProps {
  steps: StepperStep[]
  currentStep: number
  className?: string
  size?: 'md' | 'sm'
  ariaLabel?: string
}

function Stepper({
  steps,
  currentStep,
  className,
  size = 'md',
  ariaLabel = 'Progress',
}: StepperProps) {
  if (size === 'sm') {
    return (
      <ol
        aria-label={ariaLabel}
        className={cn('flex items-center gap-3 text-xs font-medium', className)}
      >
        {steps.map((step, index) => {
          const isCompleted = index < currentStep
          const isCurrent = index === currentStep
          const isLast = index === steps.length - 1

          return (
            <React.Fragment key={step.id}>
              <li
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  'flex items-center gap-2',
                  !isCurrent && !isCompleted && 'text-muted-foreground'
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full',
                    isCurrent || isCompleted
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary'
                  )}
                >
                  {isCompleted ? (
                    <Check aria-hidden="true" className="h-3 w-3" />
                  ) : (
                    index + 1
                  )}
                </span>
                {step.label}
              </li>
              {!isLast && (
                <ArrowRight
                  aria-hidden="true"
                  className="h-3.5 w-3.5 text-muted-foreground"
                />
              )}
            </React.Fragment>
          )
        })}
      </ol>
    )
  }

  return (
    <div
      role="list"
      aria-label={ariaLabel}
      className={cn('flex items-start justify-center', className)}
    >
      {steps.map((step, index) => {
        const isCompleted = index < currentStep
        const isCurrent = index === currentStep
        const isLast = index === steps.length - 1

        return (
          <React.Fragment key={step.id}>
            {/* Step with circle and label */}
            <div
              role="listitem"
              aria-current={isCurrent ? 'step' : undefined}
              className="flex flex-col items-center"
            >
              {/* Step circle */}
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition-all',
                  isCurrent &&
                    'bg-[#CF1F2E] text-white shadow-[0_0_0_4px_#FFC9CE]',
                  isCompleted &&
                    'bg-[#CF1F2E] text-white shadow-[0_0_0_4px_#FFC9CE]',
                  !isCompleted &&
                    !isCurrent &&
                    'bg-[#F8F8F8] border border-[#D6DAE1] text-[#D6DAE1]'
                )}
              >
                {isCompleted ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>

              {/* Step label - centered under circle */}
              <span
                className={cn(
                  'mt-2 text-xs font-bold transition-colors whitespace-nowrap',
                  (isCurrent || isCompleted) && 'text-[#CF1F2E]',
                  !isCurrent && !isCompleted && 'text-[#D6DAE1]'
                )}
              >
                {step.label}
              </span>
            </div>

            {/* Connector line between steps - aligned with circle center */}
            {!isLast && (
              <div
                className={cn('h-[2px] w-[100px] mx-2 mt-5', 'bg-[#D6DAE1]')}
              />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export { Stepper }
